<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  AltArrowLeftIcon,
  AltArrowRightIcon,
  DangerTriangleIcon,
  PenIcon,
} from '@solar-icons/vue/linear'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppSpinner from '../../primitives/AppSpinner/AppSpinner.vue'
import { useDesignRenderUrls } from '../../../composables/useDesignRenderUrls'
import type { PostDesign } from '../../../types/content'

const props = withDefaults(
  defineProps<{
    /** The design attached to the post as its artwork, or `null` to render nothing. */
    design?: PostDesign | null
    /** The label of the pill that opens the design; when set, the whole artwork is the door to its editor. */
    door?: string
  }>(),
  {
    design: null,
    door: undefined,
  },
)

const emit = defineEmits<{
  /** The artwork was opened at `page`, counted from 1. */
  open: [page: number]
}>()

const { t } = useI18n()

const currentIndex = ref(0)

const pageCount = computed(() => props.design?.pageCount ?? 0)
const designId = computed(() => props.design?.designId ?? null)
const renderVersion = computed(() => props.design?.renderVersion ?? null)
const { urls: pageUrls, loading: loadingPages } = useDesignRenderUrls(
  designId,
  renderVersion,
  pageCount,
)

watch(
  () => props.design,
  () => {
    currentIndex.value = 0
  },
)

const currentUrl = computed(() => pageUrls.value[currentIndex.value] ?? null)
const currentError = computed(
  () => !loadingPages.value && pageUrls.value.length > 0 && currentUrl.value === null,
)

function step(delta: number): void {
  const total = pageCount.value
  if (total <= 1) return
  currentIndex.value = (currentIndex.value + delta + total) % total
}

function goTo(index: number): void {
  currentIndex.value = index
}

function open(): void {
  if (props.door) emit('open', currentIndex.value + 1)
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' && event.target === event.currentTarget) {
    if (!props.door) return
    event.preventDefault()
    open()
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault()
    step(-1)
  } else if (event.key === 'ArrowRight') {
    event.preventDefault()
    step(1)
  }
}
</script>

<template>
  <div
    v-if="design && pageCount > 0"
    role="group"
    tabindex="0"
    :aria-label="t('content.artwork.groupAria')"
    class="group relative w-full overflow-hidden rounded-4 bg-bg-secondary outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-blue"
    :class="door ? 'cursor-pointer' : undefined"
    @click="open"
    @keydown="onKeydown"
  >
    <div class="aspect-portrait w-full">
      <div v-if="loadingPages" class="flex size-full items-center justify-center">
        <AppSpinner :label="t('content.artwork.loading')" />
      </div>
      <div
        v-else-if="currentError"
        class="flex size-full flex-col items-center justify-center gap-4 p-16 text-center"
      >
        <DangerTriangleIcon class="text-icon-tertiary" />
        <p class="text-body-sm text-text-secondary">{{ t('content.artwork.loadError') }}</p>
      </div>
      <img
        v-else-if="currentUrl"
        :src="currentUrl"
        :alt="t('content.artwork.pageAlt', { page: currentIndex + 1 })"
        class="size-full object-cover"
      />
    </div>

    <template v-if="pageCount > 1 && !loadingPages">
      <div class="absolute inset-y-0 left-8 flex items-center">
        <AppButton
          size="sm"
          icon-only
          variant="secondary"
          :start-icon="AltArrowLeftIcon"
          :aria-label="t('content.artwork.previousPage')"
          @click.stop="step(-1)"
        />
      </div>
      <div class="absolute inset-y-0 right-8 flex items-center">
        <AppButton
          size="sm"
          icon-only
          variant="secondary"
          :start-icon="AltArrowRightIcon"
          :aria-label="t('content.artwork.nextPage')"
          @click.stop="step(1)"
        />
      </div>
      <div class="absolute inset-x-0 bottom-8 flex items-center justify-center gap-4">
        <button
          v-for="(_, index) in pageUrls"
          :key="index"
          type="button"
          class="size-8 rounded-full outline-none transition-colors duration-fast ease-standard focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue motion-reduce:transition-none"
          :class="index === currentIndex ? 'bg-bg-primary' : 'bg-bg-primary/40'"
          :aria-current="index === currentIndex ? 'true' : undefined"
          :aria-label="t('content.artwork.goToPage', { page: index + 1 })"
          @click.stop="goTo(index)"
        />
      </div>
    </template>

    <div
      v-if="door"
      class="absolute end-8 top-8 opacity-0 transition-opacity duration-fast ease-soft group-focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none"
    >
      <AppButton
        size="sm"
        variant="secondary"
        :start-icon="PenIcon"
        :label="door"
        @click.stop="open"
      />
    </div>

    <span class="sr-only" role="status">{{
      t('content.artwork.pageStatus', { page: currentIndex + 1, count: pageCount })
    }}</span>
  </div>
</template>
